import { brandLinka } from '@ads/tokens';
import { DashboardDailySales } from '@linka/domain/dtos';
import { useTranslation } from '@linka/i18n';
import { formatDate, formatNumber } from '@linka/utils';
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../../../chart/chart';
import { ChartCard, ChartCardContent, ChartCardHeader, ChartCardTitle, ExportChartTriggerButton } from '../components';

interface SalesCountByDayChartProps {
  data: DashboardDailySales;
}
export const SalesCountByDayChart = ({ data }: SalesCountByDayChartProps) => {
  const t = useTranslation();
  const dailySalesConfig = {
    date: { label: t('date') },
    salesCount: { label: t('sales'), color: brandLinka.contextualColor.interactive.primary },
    totalAmount: { label: t('total-amount'), color: brandLinka.baseColor.brand['500'] },
  };
  return (
    <ChartCard>
      <ChartCardHeader>
        <ChartCardTitle>{t('dashboard.charts.sales-by-day')}</ChartCardTitle>
        <ExportChartTriggerButton type='OVERVIEW_SALES_COUNT_BY_DAY' />
      </ChartCardHeader>
      {data.data.length === 0 ? (
        <ChartCardContent className='flex flex-1 items-center justify-center py-xl'>
          <span className='ts-paragraph-xs text-ctx-content-base'>{t('dashboard.charts.no-data')}</span>
        </ChartCardContent>
      ) : (
        <ChartCardContent className='flex'>
          <ChartContainer config={dailySalesConfig} className='w-[60px]'>
            <BarChart accessibilityLayer data={data.data} margin={{ top: 24 }}>
              <Bar dataKey='salesCount' fill='transparent' barSize={0} />
              <YAxis
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                width={60}
                tickFormatter={(value) => formatNumber(value, { notation: 'compact' })}
              />
              <XAxis dataKey='date' height={120} />
            </BarChart>
          </ChartContainer>

          <div className='flex-1 overflow-x-auto'>
            <ChartContainer config={dailySalesConfig} className='w-full' dataLength={data.data.length}>
              <BarChart accessibilityLayer data={data.data} margin={{ top: 24 }}>
                <CartesianGrid vertical={false} />
                <Bar dataKey='salesCount' fill='var(--color-salesCount)' radius={[8, 8, 0, 0]}>
                  <LabelList dataKey='salesCount' position='top' formatter={(label) => formatNumber(Number(label))} />
                </Bar>
                <XAxis
                  dataKey='date'
                  tickLine={false}
                  axisLine={false}
                  tick={{ textAnchor: 'start' }}
                  angle={45}
                  height={120}
                  tickFormatter={(label) => formatDate({ date: label, dateFormat: 'dd/MM/yyyy' }) ?? label}
                />
                <ChartTooltip
                  labelFormatter={(label) => formatDate({ date: label, dateFormat: 'dd/MM/yyyy' }) ?? label}
                  cursor={{ fill: brandLinka.contextualColor.interactive.secondary }}
                  content={<ChartTooltipContent />}
                />
              </BarChart>
            </ChartContainer>
          </div>
        </ChartCardContent>
      )}
    </ChartCard>
  );
};
